import { useRef, useEffect, useCallback } from 'react';
import { useAnimation, useReducedMotion, type MotionProps } from 'framer-motion';
import { useBreakpointValue } from '@chakra-ui/react';

export function usePushTransition({ ready }: { ready: boolean }) {
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false });
  const reduceMotion = useReducedMotion();
  const controls = useAnimation();
  const animTypeRef = useRef<'push' | 'crossfade' | 'none' | null>(null);

  if (animTypeRef.current === null && ready && isMobile !== undefined) {
    animTypeRef.current = reduceMotion ? 'none' : isMobile ? 'push' : 'crossfade';
  }
  const animType = animTypeRef.current ?? 'none';

  const hasAnimatedIn = useRef(false);
  useEffect(() => {
    if (hasAnimatedIn.current || animTypeRef.current !== 'push' || !ready || isMobile === undefined) return;
    hasAnimatedIn.current = true;
    document.body.style.overflowX = 'hidden';
    void controls.start({ x: 0, transition: { duration: 0.28, ease: [0.22, 0.61, 0.36, 1] } }).then(() => {
      document.body.style.overflowX = '';
    });
    return () => { document.body.style.overflowX = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, isMobile]);

  const handleBack = useCallback(async () => {
    if (animTypeRef.current === 'push') {
      document.body.style.overflowX = 'hidden';
      await controls.start({ x: '100%', transition: { duration: 0.26, ease: [0.55, 0, 0.2, 1] } });
      document.body.style.overflowX = '';
    }
    window.history.back();
  }, [controls]);

  const motionProps: MotionProps =
    animType === 'push'
      ? { initial: { x: '100%' }, animate: controls }
      : animType === 'crossfade'
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.15, ease: 'easeOut' } }
      : {};

  return { motionProps, handleBack };
}
